import React, { useCallback, useEffect, useRef, useState } from "react";
import { useEscapeKey } from "../../../hooks/useEscapeKey";
import { AnimatePresence, motion } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenRoundedIcon from "@mui/icons-material/FullscreenRounded";
import FullscreenExitRoundedIcon from "@mui/icons-material/FullscreenExitRounded";
import CircularProgress from "@mui/material/CircularProgress";
import QRCodeStyling from "qr-code-styling";
import api from "../../../services/api";
import DropDownListCustomized from "../../ui/kit-mdtw/DropDownListCustomized";
import { FILTER_PROFILE, PROFILE_ADMIN_SEL } from "../../../diccionary/Diccionary";
import RichTextEditor from "./RichTextEditor";
import ClientLogoEditor from "./ClientLogoEditor";
import FormularioModalTabs, { FormularioModalTab } from "./FormularioModalTabs";
import type { ClientLogo } from "./clientLogo";
import FormThemeEditor from "./FormThemeEditor";
import type { FormTheme } from "./formTheme";
import {
  JOB_OFFER_DESCRIPTION_MAX,
  DescriptionSection,
  PlainDescriptionField,
} from "./descriptionFields";
import {
  buildInitialState,
  buildDisabledState,
  normalizeParentsFromChildren,
} from "./formConfigurationUtils";
import type { CampoItem, CampoState } from "./formConfigurationUtils";
import {
  OPTION_FIELD_KEYS,
  formatMissingOptionFieldsError,
  getEnabledOptionFieldsWithoutSelection,
} from "./formOptionValidation";
import type { OptionFieldKey } from "./formOptionValidation";
import { getUserErrorMessage, readApiErrorMessage } from "./postulacionesUtils";
export {
  buildInitialState,
  buildDisabledState,
  normalizeParentsFromChildren,
} from "./formConfigurationUtils";
export type { CampoItem, CampoState } from "./formConfigurationUtils";
// Logo del centro del QR — el círculo de fondo se pinta con el degradado
// de la paleta elegida para que combine con el color del QR.
const makeTwLogoDataUrl = (start: string, end: string): string => {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 657 659">` +
      `<defs>` +
        `<linearGradient id="lg" x1="114" y1="62" x2="515" y2="606" gradientUnits="userSpaceOnUse">` +
          `<stop offset="0" stop-color="${start}"/>` +
          `<stop offset="1" stop-color="${end}"/>` +
        `</linearGradient>` +
        `<clipPath id="cc">` +
          `<circle cx="326" cy="329" r="318"/>` +
        `</clipPath>` +
      `</defs>` +
      `<circle cx="326" cy="329" r="318" fill="url(#lg)"/>` +
      `<g fill="#fff" clip-path="url(#cc)">` +
        `<circle cx="235.5" cy="215.5" r="69.5"/>` +
        `<circle cx="417.5" cy="215.5" r="69.5"/>` +
        `<path fill-rule="evenodd" d="M126 649V443c0-65 48-117 110-117 39 0 73 17 90 44 18-27 52-44 91-44 62 0 110 52 110 117v206h-78V442c0-25-18-43-41-43s-41 18-41 43v207h-81V442c0-25-18-43-41-43s-41 18-41 43v207z"/>` +
      `</g>` +
    `</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};
import "../../../assets/css/ui-mdtw.min.css";

// ─── types ────────────────────────────────────────────────────────────────────

export type TipoFormulario = "cliente" | "cuenta" | "reclutador";
export type ModoFormulario = "completo" | "dividido";

// ─── API types ────────────────────────────────────────────────────────────────

interface ICliente {
  clienteId: string;
  clienteNombre: string;
  clienteDni: string;
}

interface IAreaNegocio {
  areaNegocioId: string;
  areaNegocioCod: string;
  areaNegocioNombre: string;
}

interface IUsuario {
  usuarioId: string;
  usuarioNombre: string;
}

interface IProcesoAsignado {
  busquedaId: string;
  areaNegocioCod: string;
  clienteNombre: string;
  cargoNombre: string;
}

interface IFuenteReclutamiento {
  recruitmentSourceId: string;
  name: string;
}

interface ICategoriaFormulario {
  formCategoryId: string;
  name: string;
}

interface SuccessData {
  links: string[];
  label: React.ReactNode;
  isDividido: boolean;
  code?: string;
}

// ─── field definitions ────────────────────────────────────────────────────────

export const CAMPOS: CampoItem[] = [
  { key: "disponibilidad",      label: "Disponibilidad" },
  { key: "turno",               label: "Turno" },
  { key: "cargo",               label: "Cargo" },
  { key: "estadoCivil",         label: "Estado civil" },
  { key: "numHijos",            label: "Número de hijos" },
  { key: "sistemaSalud",        label: "Sistema de salud" },
  { key: "afp",                 label: "AFP" },
  { key: "banco",               label: "Banco", childKeys: ["tipoCuenta", "numCuenta"] },
  { key: "tipoCuenta",          label: "Tipo cuenta bancaria",       isChild: true, parentKey: "banco" },
  { key: "numCuenta",           label: "Número de cuenta bancaria",  isChild: true, parentKey: "banco" },
  { key: "tallas",              label: "Tallas", childKeys: ["tallaPolera", "tallaPantalon", "numCalzado"] },
  { key: "tallaPolera",         label: "Talla de polera",   isChild: true, parentKey: "tallas" },
  { key: "tallaPantalon",       label: "Talla de pantalón", isChild: true, parentKey: "tallas" },
  { key: "numCalzado",          label: "Número de calzado", isChild: true, parentKey: "tallas" },
  { key: "contactoEmergencia",  label: "Contacto de emergencia" },
  { key: "enfermedad",          label: "Enfermedad" },
  { key: "discapacidad",        label: "Discapacidad" },
];

export const DOCUMENTOS: CampoItem[] = [
  { key: "cedulaIdentidad",   label: "Cédula de identidad" },
  { key: "licenciaConducir",  label: "Licencia de conducir" },
  { key: "curriculumVitae",   label: "Curriculum vitae" },
];

// Campos que siempre se incluyen y siempre son obligatorios — no aparecen en la lista
// configurable porque vienen por defecto en el formulario.
export const CAMPOS_OBLIGATORIOS_DEFAULT: string[] = [
  "RUT",
  "Nombres",
  "Apellidos",
  "Fecha de nacimiento",
  "Género",
  "Nacionalidad",
  "Correo electrónico",
  "Teléfono",
  "Dirección (comuna, ciudad y región)",
];

// ─── API mappings ─────────────────────────────────────────────────────────────

export const FORM_TYPE_MAP: Record<TipoFormulario, string> = {
  cliente: "CLIENTE",
  cuenta: "CUENTA",
  reclutador: "RECLUTADOR",
};

export const CAMPO_API_MAP: Record<string, string | null> = {
  disponibilidad:     "DISPONIBILIDAD",
  turno:              "TURNO",
  cargo:              "CARGO",
  estadoCivil:        "ESTADO_CIVIL",
  numHijos:           "NUMERO_HIJOS",
  sistemaSalud:       "SISTEMA_SALUD",
  afp:                "AFP",
  banco:              "BANCO",
  tipoCuenta:         "TIPO_CUENTA",
  numCuenta:          "NUMERO_CUENTA",
  tallas:             null, // agrupación UI, sin campo API directo
  tallaPolera:        "CAMISETA",
  tallaPantalon:      "PANTALON",
  numCalzado:         "ZAPATOS",
  contactoEmergencia: "CONTACTO_EMERGENCIA",
  enfermedad:         "ENFERMEDAD",
  discapacidad:       "DISCAPACIDAD",
};

export const DOCUMENTO_API_MAP: Record<string, string> = {
  cedulaIdentidad:  "IDENTIFICACION",
  licenciaConducir: "LICENCIA_CONDUCIR",
  curriculumVitae:  "CURRICULUM_VITAE",
};

export const addStep = (link: string, step: number): string => {
  const sep = link.includes("?") ? "&" : "?";
  return `${link}${sep}step=${step}`;
};

// ─── toggle switch ────────────────────────────────────────────────────────────

export const Toggle = ({
  checked,
  onChange,
  disabled,
  small,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  small?: boolean;
}) => {
  const w = small ? 30 : 36;
  const h = small ? 17 : 20;
  const d = small ? 13 : 16;
  const gap = 2;

  return (
    <button
      onClick={disabled ? undefined : onChange}
      style={{
        position: "relative",
        width: `${w}px`,
        height: `${h}px`,
        borderRadius: `${h / 2}px`,
        border: "none",
        cursor: disabled ? "default" : "pointer",
        background: checked ? "#0065DD" : "#cbd5e1",
        transition: "background 0.2s",
        flexShrink: 0,
        opacity: disabled ? 0.38 : 1,
        padding: 0,
      }}
    >
      <span
        style={{
          position: "absolute",
          top: `${gap}px`,
          left: checked ? `${w - d - gap}px` : `${gap}px`,
          width: `${d}px`,
          height: `${d}px`,
          borderRadius: "50%",
          background: "#fff",
          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
          transition: "left 0.2s",
        }}
      />
    </button>
  );
};

// ─── styles ───────────────────────────────────────────────────────────────────

export const selectStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 32px 8px 12px",
  border: "1px solid #e2e8f0",
  borderRadius: "8px",
  fontFamily: "Roboto, sans-serif",
  fontSize: "13px",
  color: "#334155",
  background: "#fff",
  cursor: "pointer",
  outline: "none",
  appearance: "none",
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 10px center",
};

export const labelStyle: React.CSSProperties = {
  fontFamily: "Roboto, sans-serif",
  fontSize: "11px",
  fontWeight: 600,
  color: "#64748b",
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  marginBottom: "5px",
};


// ─── field row ────────────────────────────────────────────────────────────────

export const FieldRow = ({
  item,
  state,
  onToggle,
  lockedByLabel,
}: {
  item: CampoItem;
  state: Record<string, CampoState>;
  onToggle: (key: string, field: "enabled" | "required") => void;
  lockedByLabel?: string;
}) => {
  const s = state[item.key];
  const locked = !!lockedByLabel;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "7px 12px",
        paddingLeft: item.isChild ? "24px" : "12px",
        borderRadius: "8px",
        background: locked ? "transparent" : s.enabled ? "rgba(0,101,221,0.04)" : "transparent",
        marginLeft: item.isChild ? "16px" : "0",
        borderLeft: item.isChild ? "2px solid #e2e8f0" : "none",
        transition: "background 0.15s",
        opacity: locked ? 0.42 : 1,
      }}
    >
      {/* left: toggle + label */}
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <Toggle
          checked={locked ? false : s.enabled}
          onChange={locked ? () => {} : () => onToggle(item.key, "enabled")}
          disabled={locked}
        />
        <span
          style={{
            fontFamily: "Roboto, sans-serif",
            fontSize: item.isChild ? "12.5px" : "13px",
            color: locked ? "#94a3b8" : s.enabled ? "#1e293b" : "#94a3b8",
            fontWeight: !locked && s.enabled ? 500 : 400,
            transition: "color 0.15s",
          }}
        >
          {item.label}
        </span>
        {locked && (
          <span
            style={{
              fontSize: "10px",
              color: "#64748b",
              background: "#f1f5f9",
              border: "1px solid #e2e8f0",
              padding: "1px 7px",
              borderRadius: "10px",
              fontFamily: "Roboto, sans-serif",
              fontWeight: 500,
              letterSpacing: "0.02em",
              whiteSpace: "nowrap",
            }}
          >
            {lockedByLabel}
          </span>
        )}
      </div>

      {/* right: obligatorio */}
      <div style={{ display: "flex", alignItems: "center", gap: "7px", flexShrink: 0 }}>
        <span
          style={{
            fontFamily: "Roboto, sans-serif",
            fontSize: "11px",
            color: !locked && s.enabled && s.required ? "#b45309" : "#cbd5e1",
            fontWeight: !locked && s.enabled && s.required ? 600 : 400,
            transition: "color 0.15s",
            whiteSpace: "nowrap",
          }}
        >
          Obligatorio
        </span>
        <Toggle
          small
          checked={locked ? false : s.required}
          onChange={locked ? () => {} : () => onToggle(item.key, "required")}
          disabled={locked || !s.enabled}
        />
      </div>
    </div>
  );
};

// ─── field options (turno / cargo) — catálogo maestro + selección ───────────────

export type OpcionCampoKey = OptionFieldKey;

export const OPCION_CAMPOS: OpcionCampoKey[] = [...OPTION_FIELD_KEYS];

// Segmento de endpoint del catálogo maestro por campo.
export const CATALOG_ENDPOINT: Record<OpcionCampoKey, string> = {
  turno: "shifts",
  cargo: "positions",
};

// Item del catálogo maestro global (compartido entre todos los formularios).
export interface CatalogItem {
  catalogItemId: string;
  name: string;
}

// Opción seleccionada para un formulario concreto (subconjunto del maestro).
export interface SelectedOption {
  catalogItemId: string;
  value: string;
}

export type CatalogosState = Record<OpcionCampoKey, CatalogItem[]>;
export type SeleccionState = Record<OpcionCampoKey, SelectedOption[]>;

export const buildInitialCatalogos = (): CatalogosState => ({ turno: [], cargo: [] });
export const buildInitialSeleccion = (): SeleccionState => ({ turno: [], cargo: [] });

// Normaliza un item del catálogo maestro tolerando las distintas claves de id
// del backend (shiftId / positionId) y de datos ya normalizados.
const normalizeCatalogItem = (o: any): CatalogItem | null => {
  const id = o?.shiftId ?? o?.positionId ?? o?.catalogItemId ?? o?.id;
  const name = o?.name ?? o?.value;
  if (typeof id !== "string" || typeof name !== "string") return null;
  return { catalogItemId: id, name };
};

// Convierte la respuesta de GET /catalogs/{shifts|positions} en CatalogItem[].
export const parseCatalogResponse = (field: OpcionCampoKey, data: any): CatalogItem[] => {
  const arr = field === "turno" ? data?.shifts : data?.positions;
  return Array.isArray(arr)
    ? arr.map(normalizeCatalogItem).filter((i): i is CatalogItem => i !== null)
    : [];
};

// GET del catálogo maestro de un campo. Mismo patrón de fetch que el resto de
// modales (sólo Content-Type, sin x-api-key).
export const fetchCatalogo = async (field: OpcionCampoKey): Promise<CatalogItem[]> => {
  const baseUrl = process.env.REACT_APP_API_APPLICANT;
  if (!baseUrl) return [];
  const res = await fetch(`${baseUrl}/catalogs/${CATALOG_ENDPOINT[field]}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return parseCatalogResponse(field, await res.json());
};

// POST para agregar un item nuevo al catálogo maestro. El backend deduplica por
// nombre (devuelve el existente si ya existe).
export const postCatalogItem = async (
  field: OpcionCampoKey,
  name: string,
  userId: string,
): Promise<CatalogItem | null> => {
  const baseUrl = process.env.REACT_APP_API_APPLICANT;
  if (!baseUrl) return null;
  const res = await fetch(
    `${baseUrl}/catalogs/${CATALOG_ENDPOINT[field]}?userId=${encodeURIComponent(userId)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  const raw = field === "turno" ? data?.shift : data?.position;
  return normalizeCatalogItem(raw);
};

interface IFieldOptionApi {
  id?: string;
  catalogItemId?: string;
  value: string;
  order?: number;
}

// Convierte la respuesta de GET /form/options ({ TURNO: [...], CARGO: [...] })
// en la selección por formulario (ordenada por `order`).
export const parseSeleccionResponse = (data: any): SeleccionState => {
  const pick = (arr: IFieldOptionApi[] | undefined | null): SelectedOption[] =>
    Array.isArray(arr)
      ? [...arr]
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .filter((o) => typeof o?.value === "string")
          .map((o) => ({ catalogItemId: o.catalogItemId ?? "", value: o.value }))
      : [];
  return { turno: pick(data?.TURNO), cargo: pick(data?.CARGO) };
};

// Construye el body `fields` para POST /form/options; sólo incluye los campos
// que `isEnabled` marque como activos. Cada opción lleva catalogItemId + value.
export const buildSeleccionFields = (
  seleccion: SeleccionState,
  isEnabled: (key: OpcionCampoKey) => boolean,
): Array<{ field: string; options: Array<{ catalogItemId: string; value: string; order: number }> }> => {
  const fields: Array<{
    field: string;
    options: Array<{ catalogItemId: string; value: string; order: number }>;
  }> = [];
  OPCION_CAMPOS.forEach((key) => {
    if (!isEnabled(key)) return;
    fields.push({
      field: CAMPO_API_MAP[key] as string,
      options: seleccion[key].map((o, idx) => ({
        catalogItemId: o.catalogItemId,
        value: o.value,
        order: idx,
      })),
    });
  });
  return fields;
};

// Hook compartido (Crear / Editar) que mantiene el catálogo maestro cargado y la
// selección por formulario, y expone las acciones de cargar / seleccionar / agregar.
export const useMasterOptions = (onChange?: () => void) => {
  const [catalogos, setCatalogos] = useState<CatalogosState>(buildInitialCatalogos());
  const [seleccion, setSeleccion] = useState<SeleccionState>(buildInitialSeleccion());
  const [loadingCatalogos, setLoadingCatalogos] = useState(false);
  const [addingField, setAddingField] = useState<OpcionCampoKey | null>(null);

  const loadCatalogos = useCallback(() => {
    setLoadingCatalogos(true);
    Promise.all(
      OPCION_CAMPOS.map((f) =>
        fetchCatalogo(f)
          .then((items) => [f, items] as const)
          .catch(() => [f, [] as CatalogItem[]] as const),
      ),
    )
      .then((pairs) => {
        const next = buildInitialCatalogos();
        pairs.forEach(([f, items]) => { next[f] = items; });
        setCatalogos(next);
      })
      .finally(() => setLoadingCatalogos(false));
  }, []);

  const toggleOption = useCallback(
    (field: OpcionCampoKey, item: CatalogItem) => {
      setSeleccion((prev) => {
        const cur = prev[field];
        const match = (s: SelectedOption) =>
          s.catalogItemId === item.catalogItemId || s.value.toLowerCase() === item.name.toLowerCase();
        const exists = cur.some(match);
        const next = exists
          ? cur.filter((s) => !match(s))
          : [...cur, { catalogItemId: item.catalogItemId, value: item.name }];
        return { ...prev, [field]: next };
      });
      onChange?.();
    },
    [onChange],
  );

  const addOption = useCallback(
    (field: OpcionCampoKey, name: string) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      // Dedupe en cliente (case-insensitive): si ya está en el catálogo, sólo se selecciona.
      const existing = catalogos[field].find(
        (c) => c.name.toLowerCase() === trimmed.toLowerCase(),
      );
      if (existing) {
        setSeleccion((prev) => {
          const cur = prev[field];
          if (
            cur.some(
              (s) =>
                s.catalogItemId === existing.catalogItemId ||
                s.value.toLowerCase() === existing.name.toLowerCase(),
            )
          ) {
            return prev;
          }
          return {
            ...prev,
            [field]: [...cur, { catalogItemId: existing.catalogItemId, value: existing.name }],
          };
        });
        onChange?.();
        return;
      }
      const userId = sessionStorage.getItem("usuarioId") ?? "";
      setAddingField(field);
      postCatalogItem(field, trimmed, userId)
        .then((item) => {
          if (!item) return;
          setCatalogos((prev) =>
            prev[field].some((c) => c.catalogItemId === item.catalogItemId)
              ? prev
              : { ...prev, [field]: [...prev[field], item] },
          );
          setSeleccion((prev) =>
            prev[field].some((s) => s.catalogItemId === item.catalogItemId)
              ? prev
              : {
                  ...prev,
                  [field]: [...prev[field], { catalogItemId: item.catalogItemId, value: item.name }],
                },
          );
          onChange?.();
        })
        .catch(() => {})
        .finally(() => setAddingField(null));
    },
    [catalogos, onChange],
  );

  return {
    catalogos,
    setCatalogos,
    seleccion,
    setSeleccion,
    loadingCatalogos,
    addingField,
    loadCatalogos,
    toggleOption,
    addOption,
  };
};

// Selector maestro de opciones para un campo (Turno / Cargo). Muestra el catálogo
// maestro como chips multi-selección y permite agregar nuevos items al catálogo.
export const MasterOptionsSelector = ({
  label,
  catalog,
  selected,
  loading,
  adding,
  error,
  onToggle,
  onAdd,
}: {
  label: string;
  catalog: CatalogItem[];
  selected: SelectedOption[];
  loading?: boolean;
  adding?: boolean;
  error?: boolean;
  onToggle: (item: CatalogItem) => void;
  onAdd: (name: string) => void;
}) => {
  const [draft, setDraft] = useState("");

  const selectedIds = new Set(selected.map((s) => s.catalogItemId));
  const selectedValues = new Set(selected.map((s) => s.value.toLowerCase()));
  const isSelected = (item: CatalogItem) =>
    selectedIds.has(item.catalogItemId) || selectedValues.has(item.name.toLowerCase());

  // Seleccionados que no están en el catálogo (datos antiguos sin catalogItemId):
  // se muestran igual como chip seleccionado por su value.
  const extra: CatalogItem[] = selected
    .filter(
      (s) =>
        !catalog.some(
          (c) => c.catalogItemId === s.catalogItemId || c.name.toLowerCase() === s.value.toLowerCase(),
        ),
    )
    .map((s) => ({ catalogItemId: s.catalogItemId || s.value, name: s.value }));
  const items = [...catalog, ...extra];

  const handleAdd = () => {
    const v = draft.trim();
    if (!v) return;
    onAdd(v);
    setDraft("");
  };

  const lower = label.toLowerCase();

  return (
    <div
      style={{
        marginLeft: "46px",
        marginTop: "2px",
        marginBottom: "4px",
        padding: "10px 12px",
        borderRadius: "8px",
        border: error ? "1px solid #ef4444" : "1px solid rgba(0,101,221,0.14)",
        background: error ? "rgba(239,68,68,0.04)" : "rgba(0,101,221,0.03)",
      }}
    >
      <div style={{ ...labelStyle, marginBottom: "7px" }}>
        {label} de este formulario
      </div>

      {/* input + agregar al catálogo maestro */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "9px" }}>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          }}
          placeholder={`Agregar ${lower} al catálogo…`}
          style={{
            flex: 1,
            padding: "8px 12px",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            fontFamily: "Roboto, sans-serif",
            fontSize: "13px",
            color: "#334155",
            background: "#fff",
            outline: "none",
          }}
        />
        <button
          onClick={handleAdd}
          disabled={draft.trim() === "" || !!adding}
          style={{
            flexShrink: 0,
            padding: "8px 16px",
            borderRadius: "8px",
            border: "none",
            background: draft.trim() === "" || adding
              ? "#cbd5e1"
              : "linear-gradient(135deg, #0050b3 0%, #0065DD 100%)",
            color: "#fff",
            fontFamily: "Roboto, sans-serif",
            fontSize: "12.5px",
            fontWeight: 600,
            cursor: draft.trim() === "" || adding ? "default" : "pointer",
            transition: "background 0.15s",
          }}
        >
          {adding ? "Agregando…" : "Agregar"}
        </button>
      </div>

      {/* catálogo maestro como chips multi-selección */}
      {loading ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontFamily: "Roboto, sans-serif",
            fontSize: "11.5px",
            color: "#94a3b8",
          }}
        >
          <CircularProgress size={13} style={{ color: "#0065DD" }} />
          Cargando catálogo…
        </div>
      ) : items.length > 0 ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {items.map((item) => {
            const sel = isSelected(item);
            return (
              <button
                key={item.catalogItemId}
                onClick={() => onToggle(item)}
                aria-pressed={sel}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "4px 11px",
                  borderRadius: "999px",
                  background: sel ? "#0065DD" : "#fff",
                  border: sel ? "1px solid #0065DD" : "1px solid #e2e8f0",
                  color: sel ? "#fff" : "#64748b",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "12.5px",
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                {item.name}
                {sel ? (
                  <CloseIcon style={{ fontSize: 12 }} />
                ) : (
                  <span style={{ fontSize: 14, lineHeight: 1, color: "#0065DD", fontWeight: 700 }}>+</span>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <div
          style={{
            fontFamily: "Roboto, sans-serif",
            fontSize: "11.5px",
            color: "#94a3b8",
            fontStyle: "italic",
          }}
        >
          El catálogo de {lower} está vacío. Agrega el primero arriba.
        </div>
      )}
      {error && (
        <div
          role="alert"
          style={{
            marginTop: "8px",
            fontFamily: "Roboto, sans-serif",
            fontSize: "11.5px",
            fontWeight: 500,
            color: "#b91c1c",
          }}
        >
          Selecciona al menos una opción para este campo.
        </div>
      )}
    </div>
  );
};

// ─── section header ───────────────────────────────────────────────────────────

export const SectionHeader = ({ title }: { title: string }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      marginBottom: "6px",
    }}
  >
    <span
      style={{
        fontFamily: "Roboto, sans-serif",
        fontSize: "11px",
        fontWeight: 700,
        color: "#475569",
        textTransform: "uppercase",
        letterSpacing: "0.1em",
      }}
    >
      {title}
    </span>
    <div style={{ flex: 1, height: "1px", background: "#f1f5f9" }} />
    <span
      style={{
        fontFamily: "Roboto, sans-serif",
        fontSize: "10px",
        color: "#94a3b8",
        whiteSpace: "nowrap",
      }}
    >
      Incluir / Obligatorio
    </span>
  </div>
);

// ─── QR link card ─────────────────────────────────────────────────────────────

const QR_DISPLAY_SIZE = 260;
const QR_EXPORT_SIZE  = 3000; // print-ready: ~25 cm @ 300 DPI

export interface QRPalette {
  start: string;
  end:   string;
}

export const DEFAULT_QR_PALETTE: QRPalette = { start: "#0050b3", end: "#06b6d4" };

const makeQRConfig = (
  size: number,
  url: string,
  palette: QRPalette = DEFAULT_QR_PALETTE,
) => ({
  width:  size,
  height: size,
  type:   "canvas",
  data:   url,
  image:  makeTwLogoDataUrl(palette.start, palette.end),
  qrOptions: { errorCorrectionLevel: "H" }, // "H" needed when logo covers the center
  dotsOptions: {
    type: "rounded",
    gradient: {
      type: "linear",
      rotation: 45,
      colorStops: [
        { offset: 0, color: palette.start },
        { offset: 1, color: palette.end   },
      ],
    },
  },
  cornersSquareOptions: {
    type: "extra-rounded",
    gradient: {
      type: "linear",
      rotation: 45,
      colorStops: [
        { offset: 0, color: palette.start },
        { offset: 1, color: palette.end   },
      ],
    },
  },
  cornersDotOptions: {
    gradient: {
      type: "radial",
      colorStops: [
        { offset: 0, color: palette.start },
        { offset: 1, color: palette.end   },
      ],
    },
  },
  imageOptions: {
    crossOrigin: "anonymous",
    margin: 2,
    imageSize: 0.42,
  },
  backgroundOptions: { color: "#ffffff" },
});

export const QRLinkCard = ({
  url,
  stepLabel,
  palette = DEFAULT_QR_PALETTE,
  exportSize = QR_EXPORT_SIZE,
  readOnly = false,
}: {
  url: string;
  stepLabel?: string;
  palette?: QRPalette;
  exportSize?: number;
  readOnly?: boolean;
}) => {
  const qrRef = useRef<HTMLDivElement>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [qrCopied, setQrCopied]     = useState(false);
  const [saving, setSaving]         = useState(false);
  const [copying, setCopying]       = useState(false);

  useEffect(() => {
    if (!qrRef.current) return;
    qrRef.current.innerHTML = "";
    const qr = new (QRCodeStyling as any)(makeQRConfig(QR_DISPLAY_SIZE, url, palette));
    qr.append(qrRef.current);
  }, [url, palette]);

  const exportName = stepLabel
    ? `qr-${stepLabel.toLowerCase().replace(/\s+/g, "-")}`
    : "qr-formulario";

  const handleCopyLink = () => {
    navigator.clipboard.writeText(url).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2200);
    });
  };

  const handleSaveQR = async () => {
    setSaving(true);
    const highRes = new (QRCodeStyling as any)(makeQRConfig(exportSize, url, palette));
    // small delay so the canvas renders the image before download
    await new Promise((r) => setTimeout(r, 400));
    highRes.download({ name: exportName, extension: "png" });
    setSaving(false);
  };

  const handleCopyQR = async () => {
    setCopying(true);
    const highRes = new (QRCodeStyling as any)(makeQRConfig(exportSize, url, palette));
    // wait for internal image to load before grabbing the blob
    await new Promise((r) => setTimeout(r, 400));
    try {
      const blob: Blob = await highRes.getRawData("png");
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      setQrCopied(true);
      setTimeout(() => setQrCopied(false), 2200);
    } catch {
      // clipboard API denied (e.g. Firefox) — fall back to download
      highRes.download({ name: exportName, extension: "png" });
    } finally {
      setCopying(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "12px",
        padding: "16px",
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: "14px",
        flex: 1,
        minWidth: 0,
      }}
    >
      {stepLabel && (
        <div
          style={{
            fontFamily: "Roboto, sans-serif",
            fontSize: "11px",
            fontWeight: 700,
            color: "#0065DD",
            background: "rgba(0,101,221,0.09)",
            padding: "3px 14px",
            borderRadius: "20px",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          {stepLabel}
        </div>
      )}

      {/* QR code container */}
      <div
        ref={qrRef}
        style={{
          borderRadius: "12px",
          overflow: "hidden",
          lineHeight: 0,
          boxShadow: "0 4px 20px rgba(0,80,179,0.15)",
          width: `${QR_DISPLAY_SIZE}px`,
          height: `${QR_DISPLAY_SIZE}px`,
          flexShrink: 0,
        }}
      />

      {/* Link row */}
      <div
        style={{
          width: "100%",
          background: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          padding: "8px 10px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          style={{
            flex: 1,
            fontFamily: "Roboto Mono, monospace",
            fontSize: "11px",
            color: "#0065DD",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textDecoration: "none",
            cursor: "pointer",
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = "underline"; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = "none"; }}
        >
          {url}
        </a>
        {!readOnly && (
          <button
            onClick={handleCopyLink}
            style={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              gap: "4px",
              padding: "4px 10px",
              borderRadius: "6px",
              border: linkCopied ? "1px solid #10b981" : "1px solid #e2e8f0",
              background: linkCopied ? "rgba(16,185,129,0.08)" : "#fff",
              color: linkCopied ? "#059669" : "#475569",
              fontFamily: "Roboto, sans-serif",
              fontSize: "11px",
              fontWeight: 500,
              cursor: "pointer",
              transition: "all 0.2s",
              whiteSpace: "nowrap",
            }}
          >
            {linkCopied ? (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Copiado
              </>
            ) : "Copiar"}
          </button>
        )}
      </div>

      {/* QR action buttons */}
      {!readOnly && (
      <div style={{ display: "flex", gap: "8px", width: "100%" }}>
        <button
          onClick={handleCopyQR}
          disabled={copying || saving}
          onMouseEnter={(e) => {
            if (!copying && !saving && !qrCopied) {
              const btn = e.currentTarget as HTMLButtonElement;
              btn.style.border = "1px solid #0065DD";
              btn.style.background = "rgba(0,101,221,0.06)";
              btn.style.color = "#0065DD";
            }
          }}
          onMouseLeave={(e) => {
            if (!copying && !saving && !qrCopied) {
              const btn = e.currentTarget as HTMLButtonElement;
              btn.style.border = "1px solid #e2e8f0";
              btn.style.background = "#fff";
              btn.style.color = "#475569";
            }
          }}
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "5px",
            padding: "7px 8px",
            borderRadius: "8px",
            border: qrCopied ? "1px solid #10b981" : "1px solid #e2e8f0",
            background: qrCopied ? "rgba(16,185,129,0.08)" : "#fff",
            color: qrCopied ? "#059669" : copying ? "#94a3b8" : "#475569",
            fontFamily: "Roboto, sans-serif",
            fontSize: "12px",
            fontWeight: 500,
            cursor: copying || saving ? "default" : "pointer",
            transition: "all 0.2s",
            whiteSpace: "nowrap",
            opacity: saving ? 0.5 : 1,
          }}
        >
          {qrCopied ? (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : copying ? (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "spin 1s linear infinite" }}>
              <circle cx="12" cy="12" r="10" strokeOpacity="0.3" />
              <path d="M12 2a10 10 0 0 1 10 10" />
            </svg>
          ) : (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
          )}
          {qrCopied ? "Copiado" : copying ? "Generando..." : "Copiar QR"}
        </button>
        <button
          onClick={handleSaveQR}
          disabled={saving || copying}
          onMouseEnter={(e) => {
            if (!saving && !copying) {
              const btn = e.currentTarget as HTMLButtonElement;
              btn.style.border = "1px solid #0065DD";
              btn.style.background = "rgba(0,101,221,0.06)";
              btn.style.color = "#0065DD";
            }
          }}
          onMouseLeave={(e) => {
            if (!saving && !copying) {
              const btn = e.currentTarget as HTMLButtonElement;
              btn.style.border = "1px solid #e2e8f0";
              btn.style.background = "#fff";
              btn.style.color = "#475569";
            }
          }}
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "5px",
            padding: "7px 8px",
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
            background: "#fff",
            color: saving ? "#94a3b8" : "#475569",
            fontFamily: "Roboto, sans-serif",
            fontSize: "12px",
            fontWeight: 500,
            cursor: saving || copying ? "default" : "pointer",
            whiteSpace: "nowrap",
            opacity: copying ? 0.5 : 1,
            transition: "all 0.2s",
          }}
        >
          {saving ? (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "spin 1s linear infinite" }}>
              <circle cx="12" cy="12" r="10" strokeOpacity="0.3" />
              <path d="M12 2a10 10 0 0 1 10 10" />
            </svg>
          ) : (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          )}
          {saving ? "Generando..." : "Guardar QR"}
        </button>
      </div>
      )}
    </div>
  );
};

// ─── main modal ───────────────────────────────────────────────────────────────

const CrearFormularioModal = ({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated?: () => void;
}) => {
  const userProfile = window.sessionStorage.getItem("profile") ?? "";
  const esAdminSel  = PROFILE_ADMIN_SEL.includes(userProfile);
  const esTwSel     = userProfile === FILTER_PROFILE.tw_sel;

  const [tipo, setTipo]                               = useState<TipoFormulario>(esTwSel ? "reclutador" : "cliente");
  const [clienteSelected, setClienteSelected]         = useState<string>("");
  const [areaSelected, setAreaSelected]               = useState<string>("");
  const [reclutadorSelected, setReclutadorSelected]   = useState<string>("");
  const [procesoSelected, setProcesoSelected]         = useState<string>("");
  const [fuenteReclutamientoSelected, setFuenteReclutamientoSelected] = useState<string>("");
  const [categoriaFormularioSelected, setCategoriaFormularioSelected] = useState<string>("");

  // Datos desde API
  const [clientes, setClientes]               = useState<ICliente[]>([]);
  const [areasNegocio, setAreasNegocio]       = useState<IAreaNegocio[]>([]);
  const [reclutadores, setReclutadores]             = useState<IUsuario[]>([]);
  const [procesosAsignados, setProcesosAsignados]   = useState<IProcesoAsignado[]>([]);
  const [loadingClientes, setLoadingClientes]       = useState(false);
  const [loadingAreas, setLoadingAreas]             = useState(false);
  const [loadingReclutadores, setLoadingReclutadores] = useState(false);
  const [loadingProcesos, setLoadingProcesos]       = useState(false);
  const [loadingMoreProcesos, setLoadingMoreProcesos] = useState(false);
  const [procesoPage, setProcesoPage]               = useState(1);
  const [procesoSearch, setProcesoSearch]           = useState("");
  const [procesoHasNextPage, setProcesoHasNextPage] = useState(false);
  const [fuentesReclutamiento, setFuentesReclutamiento] = useState<IFuenteReclutamiento[]>([]);
  const [loadingFuentesReclutamiento, setLoadingFuentesReclutamiento] = useState(false);
  const [categoriasFormulario, setCategoriasFormulario] = useState<ICategoriaFormulario[]>([]);
  const [loadingCategoriasFormulario, setLoadingCategoriasFormulario] = useState(false);

  // DropDownListCustomized form states
  const [clienteFormVals, setClienteFormVals]       = useState<any>({ cliente: null });
  const [areaFormVals, setAreaFormVals]             = useState<any>({ area: null });
  const [reclutadorFormVals, setReclutadorFormVals] = useState<any>({ reclutador: null });
  const [procesoFormVals, setProcesoFormVals]       = useState<any>({ proceso: null });
  const [fuenteFormVals, setFuenteFormVals]         = useState<any>({ fuente: null });
  const [categoriaFormVals, setCategoriaFormVals]   = useState<any>({ categoria: null });

  const [asignarAMi, setAsignarAMi] = useState(false);
  // Guarda la última selección real para restaurarla al desmarcar el toggle
  const [reclutadorSaved, setReclutadorSaved] = useState<{ name: string; code: string } | null>(null);

  // Form structure mode
  const [modoFormulario, setModoFormularioState]      = useState<ModoFormulario>("completo");
  const [pasoActivo, setPasoActivo]                   = useState<1 | 2>(1);
  const [modalTab, setModalTab]                       = useState<FormularioModalTab>("configuracion");
  const [isMaximized, setIsMaximized]                 = useState(false);
  const [previewHost, setPreviewHost]                 = useState<HTMLDivElement | null>(null);

  // Optional descriptions (toggle-gated)
  const [formDescEnabled, setFormDescEnabled]                 = useState(false);
  const [formDescription, setFormDescription]                 = useState("");
  const [jobOfferDescEnabled, setJobOfferDescEnabled]         = useState(false);
  const [jobOfferDescription, setJobOfferDescription]         = useState("");
  const [clientLogo, setClientLogo] = useState<ClientLogo | null>(null);
  const [formTheme, setFormTheme] = useState<FormTheme | null>(null);
  const [logoEnabled, setLogoEnabled] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);

  // Submission state
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const [showOptionErrors, setShowOptionErrors] = useState(false);
  const [successData, setSuccessData]   = useState<SuccessData | null>(null);

  const [hasChanges, setHasChanges]             = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [showDefaultFields, setShowDefaultFields] = useState(false);

  const markDirty = () => { if (!hasChanges) setHasChanges(true); };

  const handleRequestClose = () => {
    if (hasChanges && !successData) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  // Esc: cierra la confirmación si está abierta; si no, igual que la X (con
  // cambios pregunta antes). Mientras se crea el formulario no hace nada.
  useEscapeKey(() => {
    if (showConfirmClose) { setShowConfirmClose(false); return; }
    if (loading) return;
    handleRequestClose();
  });

  // Field-level validation errors
  const [clienteErr, setClienteErr]     = useState(false);
  const [areaErr, setAreaErr]           = useState(false);
  const [reclutadorErr, setReclutadorErr] = useState(false);
  const [procesoErr, setProcesoErr]     = useState(false);

  // Paso 1 / completo fields
  const [campos, setCampos]         = useState<Record<string, CampoState>>(buildInitialState(CAMPOS));
  const [documentos, setDocumentos] = useState<Record<string, CampoState>>(buildInitialState(DOCUMENTOS));

  // Paso 2 fields (dividido mode only, starts all-disabled)
  const [camposPaso2, setCamposPaso2]         = useState<Record<string, CampoState>>(buildDisabledState(CAMPOS));
  const [documentosPaso2, setDocumentosPaso2] = useState<Record<string, CampoState>>(buildDisabledState(DOCUMENTOS));

  // Catálogo maestro (turno/cargo) + selección por formulario (dos niveles)
  const {
    catalogos,
    seleccion,
    loadingCatalogos,
    addingField,
    loadCatalogos,
    toggleOption,
    addOption,
  } = useMasterOptions(markDirty);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Carga el catálogo maestro de turnos/cargos al montar el modal
  useEffect(() => {
    loadCatalogos();
  }, [loadCatalogos]);

  useEffect(() => {
    const baseUrl = process.env.REACT_APP_API_APPLICANT;
    if (!baseUrl) return;
    setLoadingFuentesReclutamiento(true);
    fetch(`${baseUrl}/catalogs/recruitment-sources`)
      .then(async (res) => {
        if (!res.ok) throw new Error("No se pudo cargar el catálogo");
        return res.json();
      })
      .then((data) => setFuentesReclutamiento(
        Array.isArray(data?.recruitmentSources) ? data.recruitmentSources : [],
      ))
      .catch(() => setFuentesReclutamiento([]))
      .finally(() => setLoadingFuentesReclutamiento(false));
  }, []);

  useEffect(() => {
    const baseUrl = process.env.REACT_APP_API_APPLICANT;
    if (!baseUrl) return;
    setLoadingCategoriasFormulario(true);
    fetch(`${baseUrl}/catalogs/form-categories`)
      .then(async (res) => {
        if (!res.ok) throw new Error("No se pudo cargar el catálogo");
        return res.json();
      })
      .then((data) => setCategoriasFormulario(
        Array.isArray(data?.formCategories) ? data.formCategories : [],
      ))
      .catch(() => setCategoriasFormulario([]))
      .finally(() => setLoadingCategoriasFormulario(false));
  }, []);

  useEffect(() => {
    setClienteSelected("");
    setAreaSelected("");
    setReclutadorSelected("");
    setProcesoSelected("");
    setClienteFormVals({ cliente: null });
    setAreaFormVals({ area: null });
    setReclutadorFormVals({ reclutador: null });
    setProcesoFormVals({ proceso: null });
    setError(null);
    setClienteErr(false);
    setAreaErr(false);
    setReclutadorErr(false);
    setProcesoErr(false);
  }, [tipo]);

  // Sync DropDownListCustomized selections → string state (used in API calls)
  useEffect(() => {
    setClienteSelected(clienteFormVals.cliente?.value?.code ?? "");
    if (clienteFormVals.cliente?.value?.code) { setClienteErr(false); setError(null); }
  }, [clienteFormVals]);

  useEffect(() => {
    setAreaSelected(areaFormVals.area?.value?.code ?? "");
    if (areaFormVals.area?.value?.code) { setAreaErr(false); setError(null); }
  }, [areaFormVals]);

  const fetchProcesos = (
    recruiterId: string,
    loggedUserId: string,
    page: number,
    search: string,
    append: boolean
  ) => {
    const params: Record<string, any> = { userId: loggedUserId, recruiter: recruiterId, page, limit: 1000 };
    if (search) params.search = search;
    if (append) {
      setLoadingMoreProcesos(true);
    } else {
      setLoadingProcesos(true);
    }
    api
      .get("procesos-asignados", { params })
      .then((res) => {
        const data: IProcesoAsignado[] = res.data.data ?? [];
        const pagination = res.data.pagination;
        setProcesoHasNextPage(pagination?.hasNextPage ?? false);
        setProcesoPage(pagination?.currentPage ?? page);
        if (append) {
          setProcesosAsignados((prev) => [...prev, ...data]);
        } else {
          setProcesosAsignados(data);
        }
      })
      .catch(() => { if (!append) setProcesosAsignados([]); })
      .finally(() => {
        if (append) setLoadingMoreProcesos(false);
        else setLoadingProcesos(false);
      });
  };

  const handleProcesoSearch = (value: string) => {
    setProcesoSearch(value);
    const loggedUserId = sessionStorage.getItem("usuarioId") ?? "";
    const recruiterId = asignarAMi ? loggedUserId : reclutadorSelected;
    if (!recruiterId) return;
    fetchProcesos(recruiterId, loggedUserId, 1, value, false);
  };

  const handleProcesoLoadMore = () => {
    if (!procesoHasNextPage || loadingMoreProcesos) return;
    const loggedUserId = sessionStorage.getItem("usuarioId") ?? "";
    const recruiterId = asignarAMi ? loggedUserId : reclutadorSelected;
    if (!recruiterId) return;
    const nextPage = procesoPage + 1;
    fetchProcesos(recruiterId, loggedUserId, nextPage, procesoSearch, true);
  };

  useEffect(() => {
    const id = reclutadorFormVals.reclutador?.value?.code ?? "";
    setReclutadorSelected(id);
    if (id) { setReclutadorErr(false); setError(null); }
    // Resetear proceso al cambiar reclutador
    setProcesoFormVals({ proceso: null });
    setProcesoSelected("");
    setProcesosAsignados([]);
    setProcesoSearch("");
    setProcesoPage(1);
    setProcesoHasNextPage(false);
    if (tipo !== "reclutador") return;
    const loggedUserId = sessionStorage.getItem("usuarioId") ?? "";
    const recruiterId = asignarAMi ? loggedUserId : id;
    if (!recruiterId) return;
    fetchProcesos(recruiterId, loggedUserId, 1, "", false);
  }, [reclutadorFormVals, asignarAMi]);

  useEffect(() => {
    setProcesoSelected(procesoFormVals.proceso?.value?.code ?? "");
    if (procesoFormVals.proceso?.value?.code) { setProcesoErr(false); setError(null); }
  }, [procesoFormVals]);

  useEffect(() => {
    setFuenteReclutamientoSelected(fuenteFormVals.fuente?.value?.code ?? "");
  }, [fuenteFormVals]);

  useEffect(() => {
    setCategoriaFormularioSelected(categoriaFormVals.categoria?.value?.code ?? "");
  }, [categoriaFormVals]);

  useEffect(() => {
    setAreaSelected("");
    setAreasNegocio([]);
  }, [clienteSelected]);

  // Carga lista de clientes al abrir el modal
  useEffect(() => {
    const userId = sessionStorage.getItem("usuarioId");
    setLoadingClientes(true);
    api
      .get("cliente", { params: { userId } })
      .then((res) => setClientes(res.data.data ?? []))
      .catch(() => setClientes([]))
      .finally(() => setLoadingClientes(false));
  }, []);

  // Carga áreas de negocio cuando se selecciona un cliente (modo cuenta)
  useEffect(() => {
    if (!clienteSelected || tipo !== "cuenta") return;
    const userId = sessionStorage.getItem("usuarioId");
    setLoadingAreas(true);
    api
      .get("area-negocio-cliente", { params: { userId, clienteId: clienteSelected } })
      .then((res) => setAreasNegocio(res.data ?? []))
      .catch(() => setAreasNegocio([]))
      .finally(() => setLoadingAreas(false));
  }, [clienteSelected, tipo]);

  // Carga lista de reclutadores cuando se activa el tipo "reclutador" (solo perfiles admin)
  useEffect(() => {
    if (tipo !== "reclutador" || !esAdminSel) return;
    const userId = sessionStorage.getItem("usuarioId");
    setLoadingReclutadores(true);
    api
      .post(`usuarios?userId=${userId}`, {
        profiles: [
          "RECLUTADOR",
          "ADMINISTRADOR TEAMWORK",
          "ADMINISTRADOR DE SELECCIÓN MASIVA",
          "ADMINISTRADOR DE SELECCIÓN PROFESIONAL",
        ],
      })
      .then((res) => setReclutadores(res.data ?? []))
      .catch(() => setReclutadores([]))
      .finally(() => setLoadingReclutadores(false));
  }, [tipo, esAdminSel]);

  const handleToggleAsignarAMi = () => {
    markDirty();
    setAsignarAMi((prev) => {
      const next = !prev;
      if (next) {
        // Guardar selección actual antes de bloquear
        const currentOption = reclutadorFormVals.reclutador?.value ?? null;
        setReclutadorSaved(currentOption);
      } else {
        // Al desmarcar, restaurar la selección guardada en el estado del form
        setReclutadorFormVals({ reclutador: reclutadorSaved ? { value: reclutadorSaved, valid: true } : null });
      }
      return next;
    });
  };

  // Switch mode — reset paso 2 when switching to dividido so fields start fresh
  const setModoFormulario = (mode: ModoFormulario) => {
    markDirty();
    setModoFormularioState(mode);
    setPasoActivo(1);
    if (mode === "dividido") {
      setCamposPaso2(buildDisabledState(CAMPOS));
      setDocumentosPaso2(buildDisabledState(DOCUMENTOS));
    }
  };

  // ── Paso 1 / completo toggles ─────────────────────────────────────────────

  const toggleCampo = (key: string, field: "enabled" | "required") => {
    markDirty();
    const item = CAMPOS.find((c) => c.key === key);
    const newEnabled = !campos[key].enabled;

    setCampos((prev) => {
      const next = { ...prev };
      if (field === "enabled") {
        next[key] = { enabled: newEnabled, required: newEnabled ? prev[key].required : false };
        if (item?.childKeys) {
          if (newEnabled) {
            item.childKeys.forEach((ck) => { next[ck] = { ...prev[ck], enabled: true }; });
          } else {
            item.childKeys.forEach((ck) => { next[ck] = { enabled: false, required: false }; });
          }
        }
      } else {
        if (prev[key].enabled) {
          const newRequired = !prev[key].required;
          next[key] = { ...prev[key], required: newRequired };
          item?.childKeys?.forEach((ck) => {
            if (prev[ck]?.enabled) next[ck] = { ...prev[ck], required: newRequired };
          });
        }
      }
      return normalizeParentsFromChildren(next, CAMPOS);
    });

    // When enabling in paso 1 (dividido mode), remove from paso 2
    if (field === "enabled" && newEnabled && modoFormulario === "dividido") {
      setCamposPaso2((prev) => {
        const next = { ...prev };
        next[key] = { enabled: false, required: false };
        if (item?.childKeys) {
          item.childKeys.forEach((ck) => { next[ck] = { enabled: false, required: false }; });
        }
        return normalizeParentsFromChildren(next, CAMPOS);
      });
    }
  };

  const toggleDocumento = (key: string, field: "enabled" | "required") => {
    markDirty();
    const newEnabled = !documentos[key].enabled;
    setDocumentos((prev) => {
      const next = { ...prev };
      if (field === "enabled") {
        next[key] = { enabled: newEnabled, required: newEnabled ? prev[key].required : false };
      } else {
        if (prev[key].enabled) {
          next[key] = { ...prev[key], required: !prev[key].required };
        }
      }
      return next;
    });
    if (field === "enabled" && newEnabled && modoFormulario === "dividido") {
      setDocumentosPaso2((prev) => ({ ...prev, [key]: { enabled: false, required: false } }));
    }
  };

  // ── Paso 2 toggles ────────────────────────────────────────────────────────

  const toggleCampoPaso2 = (key: string, field: "enabled" | "required") => {
    markDirty();
    const item = CAMPOS.find((c) => c.key === key);
    const newEnabled = !camposPaso2[key].enabled;

    setCamposPaso2((prev) => {
      const next = { ...prev };
      if (field === "enabled") {
        next[key] = { enabled: newEnabled, required: newEnabled ? prev[key].required : false };
        if (item?.childKeys) {
          if (newEnabled) {
            item.childKeys.forEach((ck) => { next[ck] = { ...prev[ck], enabled: true }; });
          } else {
            item.childKeys.forEach((ck) => { next[ck] = { enabled: false, required: false }; });
          }
        }
      } else {
        if (prev[key].enabled) {
          const newRequired = !prev[key].required;
          next[key] = { ...prev[key], required: newRequired };
          item?.childKeys?.forEach((ck) => {
            if (prev[ck]?.enabled) next[ck] = { ...prev[ck], required: newRequired };
          });
        }
      }
      return normalizeParentsFromChildren(next, CAMPOS);
    });

    // When enabling in paso 2, remove from paso 1
    if (field === "enabled" && newEnabled) {
      setCampos((prev) => {
        const next = { ...prev };
        next[key] = { enabled: false, required: false };
        if (item?.childKeys) {
          item.childKeys.forEach((ck) => { next[ck] = { enabled: false, required: false }; });
        }
        return normalizeParentsFromChildren(next, CAMPOS);
      });
    }
  };

  const toggleDocumentoPaso2 = (key: string, field: "enabled" | "required") => {
    markDirty();
    const newEnabled = !documentosPaso2[key].enabled;
    setDocumentosPaso2((prev) => {
      const next = { ...prev };
      if (field === "enabled") {
        next[key] = { enabled: newEnabled, required: newEnabled ? prev[key].required : false };
      } else {
        if (prev[key].enabled) {
          next[key] = { ...prev[key], required: !prev[key].required };
        }
      }
      return next;
    });
    if (field === "enabled" && newEnabled) {
      setDocumentos((prev) => ({ ...prev, [key]: { enabled: false, required: false } }));
    }
  };

  // ── Active step derivations ───────────────────────────────────────────────

  const isDividido = modoFormulario === "dividido";
  const isStep1    = !isDividido || pasoActivo === 1;

  const activeCampos          = isStep1 ? campos       : camposPaso2;
  const activeDocumentos      = isStep1 ? documentos   : documentosPaso2;
  const activeToggleCampo     = isStep1 ? toggleCampo  : toggleCampoPaso2;
  const activeToggleDocumento = isStep1 ? toggleDocumento : toggleDocumentoPaso2;
  const setActiveCampos       = isStep1 ? setCampos    : setCamposPaso2;
  const setActiveDocumentos   = isStep1 ? setDocumentos : setDocumentosPaso2;

  // Keys locked by the other step
  const lockedCampoKeys = new Set<string>();
  const lockedDocKeys   = new Set<string>();
  if (isDividido) {
    const otherCampos = pasoActivo === 1 ? camposPaso2 : campos;
    const otherDocs   = pasoActivo === 1 ? documentosPaso2 : documentos;
    CAMPOS.forEach((c) => { if (otherCampos[c.key].enabled) lockedCampoKeys.add(c.key); });
    DOCUMENTOS.forEach((d) => { if (otherDocs[d.key].enabled) lockedDocKeys.add(d.key); });
  }

  const otherStepLabel = pasoActivo === 1 ? "En Paso 2" : "En Paso 1";

  // ── Bulk action derivations ───────────────────────────────────────────────

  const areaOptions = areasNegocio;

  const unlockedCampos = CAMPOS.filter((c) => !lockedCampoKeys.has(c.key));
  const unlockedDocs   = DOCUMENTOS.filter((d) => !lockedDocKeys.has(d.key));

  const allEnabled =
    unlockedCampos.every((c) => activeCampos[c.key].enabled) &&
    unlockedDocs.every((d) => activeDocumentos[d.key].enabled);

  const enabledCampos = unlockedCampos.filter((c) => activeCampos[c.key].enabled);
  const enabledDocs   = unlockedDocs.filter((d) => activeDocumentos[d.key].enabled);
  const allRequired =
    enabledCampos.length + enabledDocs.length > 0 &&
    enabledCampos.every((c) => activeCampos[c.key].required) &&
    enabledDocs.every((d) => activeDocumentos[d.key].required);

  const toggleAllEnabled = () => {
    markDirty();
    const next = !allEnabled;
    const curCampos = activeCampos;
    const curDocs   = activeDocumentos;
    setActiveCampos(() => {
      const s: Record<string, CampoState> = {};
      CAMPOS.forEach((c) => {
        if (lockedCampoKeys.has(c.key)) {
          s[c.key] = curCampos[c.key];
        } else {
          s[c.key] = { enabled: next, required: next ? curCampos[c.key].required : false };
        }
      });
      return s;
    });
    setActiveDocumentos(() => {
      const s: Record<string, CampoState> = {};
      DOCUMENTOS.forEach((d) => {
        if (lockedDocKeys.has(d.key)) {
          s[d.key] = curDocs[d.key];
        } else {
          s[d.key] = { enabled: next, required: next ? curDocs[d.key].required : false };
        }
      });
      return s;
    });
  };

  const toggleAllRequired = () => {
    markDirty();
    const next = !allRequired;
    setActiveCampos((prev) => {
      const s: Record<string, CampoState> = {};
      CAMPOS.forEach((c) => {
        if (lockedCampoKeys.has(c.key)) {
          s[c.key] = prev[c.key];
        } else {
          s[c.key] = prev[c.key].enabled ? { ...prev[c.key], required: next } : prev[c.key];
        }
      });
      return s;
    });
    setActiveDocumentos((prev) => {
      const s: Record<string, CampoState> = {};
      DOCUMENTOS.forEach((d) => {
        if (lockedDocKeys.has(d.key)) {
          s[d.key] = prev[d.key];
        } else {
          s[d.key] = prev[d.key].enabled ? { ...prev[d.key], required: next } : prev[d.key];
        }
      });
      return s;
    });
  };

  const TIPO_LABELS: Record<TipoFormulario, string> = {
    cliente: "Cliente",
    cuenta: "Cuenta",
    reclutador: "Reclutador",
  };

  // ── Submit handler ────────────────────────────────────────────────────────

  const handleCrear = async () => {
    if (logoBusy || (logoEnabled && !clientLogo)) {
      setModalTab("identidad-visual");
      setError(logoBusy ? "Espera a que termine la carga o aplica/cancela la edición del logo antes de guardar." : "Carga un logo o desactiva la opción Logo del cliente.");
      return;
    }
    setError(null);
    setClienteErr(false);
    setAreaErr(false);
    setReclutadorErr(false);
    setProcesoErr(false);

    // ── Validación de campos requeridos por sección ──────────────────────────
    if (tipo === "cliente" && !clienteSelected) {
      setClienteErr(true);
      setError("Debes seleccionar un cliente para continuar.");
      return;
    }
    if (tipo === "cuenta") {
      if (!clienteSelected) {
        setClienteErr(true);
        setError("Debes seleccionar un cliente para continuar.");
        return;
      }
      if (!areaSelected) {
        setAreaErr(true);
        setError("Debes seleccionar un área de negocio para continuar.");
        return;
      }
    }
    if (tipo === "reclutador") {
      if (!asignarAMi && !reclutadorSelected) {
        setReclutadorErr(true);
        setError("Debes seleccionar un reclutador o activar \"Asignar a mi\".");
        return;
      }
      if (!procesoSelected) {
        setProcesoErr(true);
        setError("Debes seleccionar un proceso para continuar.");
        return;
      }
    }

    const isOptionFieldEnabled = (key: OpcionCampoKey) =>
      campos[key].enabled || (modoFormulario === "dividido" && camposPaso2[key].enabled);
    const missingOptionFields = getEnabledOptionFieldsWithoutSelection(
      seleccion,
      isOptionFieldEnabled,
    );
    setShowOptionErrors(missingOptionFields.length > 0);
    if (missingOptionFields.length > 0) {
      setError(formatMissingOptionFieldsError(missingOptionFields));
      return;
    }

    setLoading(true);

    const baseUrl = process.env.REACT_APP_API_APPLICANT;
    const loggedUserId = sessionStorage.getItem("usuarioId") ?? "";

    const recruiterValue = tipo === "reclutador"
      ? (asignarAMi ? (sessionStorage.getItem("usuarioId") ?? "") : reclutadorSelected)
      : "";

    const contextPayload = {
      formType:     FORM_TYPE_MAP[tipo],
      client:       tipo === "cliente" || tipo === "cuenta" ? clienteSelected : "",
      businessArea: tipo === "cuenta" ? areaSelected : "",
      vacancy:      tipo === "reclutador" ? procesoSelected : "",
      recruiter:    recruiterValue,
      steps:        modoFormulario === "dividido" ? 2 : 1,
      formDescription:     formDescEnabled && formDescription.trim() !== "" ? formDescription.trim() : null,
      jobOfferDescription: jobOfferDescEnabled && jobOfferDescription.trim() !== "" ? jobOfferDescription : null,
      ...(clientLogo ? { clientLogo: { ...clientLogo, enabled: logoEnabled } } : {}),
      ...(formTheme ? { formTheme } : {}),
      recruitmentSourceId: fuenteReclutamientoSelected || null,
      formCategoryId: categoriaFormularioSelected || null,
    };

    let contextId: string;
    let formLink: string;
    let formCode: string | undefined;
    let configurationStage: "campos" | "opciones" = "campos";
    try {
      const res = await fetch(`${baseUrl}/form/context?userId=${encodeURIComponent(loggedUserId)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(contextPayload),
      });
      if (!res.ok) throw new Error(await readApiErrorMessage(res));
      const data = await res.json();
      contextId = data.contextId;
      formLink  = data.link;
      formCode  = data.code;
    } catch (error) {
      setError(getUserErrorMessage(error, "Error al crear el contexto del formulario. Intenta de nuevo."));
      setLoading(false);
      return;
    }

    const buildFields = (
      camposState: Record<string, CampoState>,
      docsState: Record<string, CampoState>,
      step: "E1" | "E2",
    ) => {
      const fields: Array<{ step: string; field: string; visible: boolean; required: boolean }> = [];
      CAMPOS.forEach((item) => {
        const apiField = CAMPO_API_MAP[item.key];
        if (!apiField) return;
        const s = camposState[item.key];
        fields.push({ step, field: apiField, visible: s.enabled, required: s.required });
      });
      DOCUMENTOS.forEach((item) => {
        const apiField = DOCUMENTO_API_MAP[item.key];
        const s = docsState[item.key];
        fields.push({ step, field: apiField, visible: s.enabled, required: s.required });
      });
      return fields;
    };

    const allFields =
      modoFormulario === "completo"
        ? buildFields(campos, documentos, "E1")
        : [
            ...buildFields(campos, documentos, "E1"),
            ...buildFields(camposPaso2, documentosPaso2, "E2"),
          ];

    const configPayload = {
      configType: modoFormulario === "completo" ? "COMPLETO" : "PASOS",
      fields: allFields,
    };

    try {
      const res = await fetch(`${baseUrl}/form/config/${contextId}?userId=${encodeURIComponent(loggedUserId)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(configPayload),
      });
      if (!res.ok) throw new Error(await readApiErrorMessage(res));

      // ── Opciones de turno/cargo — sólo campos habilitados en algún paso ──
      const userId = sessionStorage.getItem("usuarioId") ?? "";
      const optionFields = buildSeleccionFields(
        seleccion,
        (key) => campos[key].enabled || (modoFormulario === "dividido" && camposPaso2[key].enabled),
      );
      if (optionFields.length > 0) {
        configurationStage = "opciones";
        const optRes = await fetch(`${baseUrl}/form/options/${contextId}?userId=${userId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ fields: optionFields }),
        });
        if (!optRes.ok) throw new Error(await readApiErrorMessage(optRes));
      }

      // Build links — always ?step=1, add ?step=2 if dividido
      const links = [addStep(formLink, 1)];
      if (modoFormulario === "dividido") links.push(addStep(formLink, 2));

      // Build contextual success label (bold on variable parts)
      let label: React.ReactNode = "Formulario creado correctamente";
      if (tipo === "cliente") {
        const clienteNombre = clientes.find((c) => c.clienteId === clienteSelected)?.clienteNombre ?? clienteSelected;
        label = <>Formulario creado correctamente para el cliente <strong>{clienteNombre}</strong></>;
      } else if (tipo === "cuenta") {
        label = <>Formulario creado correctamente para la cuenta <strong>{areaSelected}</strong></>;
      } else if (tipo === "reclutador") {
        const rName = asignarAMi
          ? (sessionStorage.getItem("usuarioNombre") ?? "ti")
          : (reclutadores.find((r) => r.usuarioId === reclutadorSelected)?.usuarioNombre ?? reclutadorSelected);
        label = <>Formulario creado correctamente para <strong>{rName}</strong>, proceso <strong>{procesoSelected}</strong></>;
      }

      setSuccessData({ links, label, isDividido: modoFormulario === "dividido", code: formCode });
      onCreated?.();
    } catch (error) {
      const detail = getUserErrorMessage(error, "Intenta nuevamente.");
      setError(configurationStage === "opciones"
        ? `El formulario se creó, pero no pudimos guardar las opciones de turno o cargo. ${detail}`
        : `El formulario se creó, pero no pudimos guardar la configuración de sus campos. ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  const splitIdentityLayout = isMaximized && modalTab === "identidad-visual" && !successData;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.55)",
        backdropFilter: "blur(5px)",
        WebkitBackdropFilter: "blur(5px)",
        zIndex: 1200,
        display: "flex",
        alignItems: successData ? "center" : "flex-start",
        justifyContent: "center",
        padding: isMaximized ? 0 : successData ? "24px" : "32px 24px 48px",
        overflowY: successData ? "hidden" : "auto",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 28, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 18, scale: 0.97 }}
        transition={{ type: "spring", stiffness: 300, damping: 26 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: isMaximized ? "100vw" : successData ? (successData.isDividido ? "860px" : "820px") : modalTab === "identidad-visual" ? "1040px" : "820px",
          height: isMaximized ? "100vh" : undefined,
          borderRadius: isMaximized ? 0 : "18px",
          overflow: "hidden",
          background: "#fff",
          boxShadow: "0 28px 64px rgba(15,23,42,0.28), 0 0 0 1px rgba(15,23,42,0.07)",
          transition: "max-width 0.2s ease",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* spin keyframe for loading icons */}
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

        {/* ── Header ── */}
        <div
          style={{
            background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)",
            padding: "22px 24px",
            position: "relative",
          }}
        >
          {!successData && <button
            type="button"
            aria-label={isMaximized ? "Salir de pantalla completa" : "Maximizar modal"}
            title={isMaximized ? "Salir de pantalla completa" : "Maximizar"}
            onClick={() => setIsMaximized((value) => !value)}
            style={{ position:"absolute", top:"14px", right:"48px", width:"28px", height:"28px", borderRadius:"50%", border:"none", background:"transparent", cursor:"pointer", color:"rgba(255,255,255,.7)", display:"flex", alignItems:"center", justifyContent:"center", padding:0 }}
          >{isMaximized ? <FullscreenExitRoundedIcon style={{fontSize:19}} /> : <FullscreenRoundedIcon style={{fontSize:19}} />}</button>}
          <button
            onClick={handleRequestClose}
            style={{
              position: "absolute", top: "14px", right: "14px",
              width: "28px", height: "28px", borderRadius: "50%",
              border: "none", background: "transparent",
              cursor: "pointer", color: "rgba(255,255,255,0.6)",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 0, lineHeight: 1, outline: "none",
              transition: "opacity 0.15s",
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = "1"; (e.currentTarget as HTMLButtonElement).style.color = "#fff"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = ""; (e.currentTarget as HTMLButtonElement).style.color = "rgba(255,255,255,0.6)"; }}
          >
            <CloseIcon style={{ fontSize: 16 }} />
          </button>
          <div style={{ fontFamily: "Roboto, sans-serif", fontSize: "16px", fontWeight: 600, color: "#fff" }}>
            Crear formulario de postulación
          </div>
          <div style={{ fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "rgba(255,255,255,0.5)", marginTop: "3px" }}>
            Configura los campos, documentos y requisitos del formulario
          </div>
        </div>

        {splitIdentityLayout && (
          <div
            ref={setPreviewHost}
            className="client-logo-preview-host"
            role="region"
            aria-label="Previsualización del formulario"
          />
        )}

        {!successData && (
          <div style={{ width: splitIdentityLayout ? "clamp(440px, 30vw, 580px)" : "100%", flexShrink: 0 }}>
            <FormularioModalTabs
              active={modalTab}
              onChange={setModalTab}
              logoEnabled={logoEnabled}
              themeCustomized={Boolean(formTheme)}
            />
          </div>
        )}

        {/* ── Body ── */}
        <div style={{ padding: "24px", width: splitIdentityLayout ? "clamp(440px, 30vw, 580px)" : "100%", boxSizing: "border-box", maxHeight: isMaximized ? "none" : successData ? "none" : "72vh", flex: isMaximized ? "1 1 auto" : undefined, minHeight: isMaximized ? 0 : undefined, overflowY: successData ? "visible" : "auto" }}>

          {/* ── Success view ── */}
          {successData && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "20px",
                padding: "24px 8px",
                textAlign: "center",
              }}
            >
              {/* Check icon */}
              <div
                style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 16px rgba(5,150,105,0.3)",
                  flexShrink: 0,
                }}
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>

              {/* Title + subtitle */}
              <div>
                <div
                  style={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "15px",
                    fontWeight: 700,
                    color: "#1e293b",
                    marginBottom: "6px",
                    lineHeight: 1.4,
                  }}
                >
                  {successData.label}
                </div>
                {successData.code && (
                  <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "4px 10px", borderRadius: "999px", background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1d4ed8", fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace", fontSize: "12.5px", fontWeight: 600, marginBottom: "8px" }}>
                    Código: {successData.code}
                  </div>
                )}
                <div style={{ fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "#64748b" }}>
                  {successData.isDividido
                    ? "Comparte los siguientes enlaces con los postulantes"
                    : "Comparte el siguiente enlace con los postulantes"}
                </div>
              </div>

              {/* QR link cards */}
              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  width: "100%",
                  flexDirection: successData.isDividido ? "row" : "column",
                  alignItems: successData.isDividido ? "stretch" : "center",
                }}
              >
                {successData.links.map((url, i) => (
                  <QRLinkCard
                    key={url}
                    url={url}
                    stepLabel={successData.isDividido ? `Paso ${i + 1}` : undefined}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ── Form content (hidden on success) ── */}
          {!successData && (modalTab === "identidad-visual" ? (
            <div
              role="tabpanel"
              id="form-modal-panel-identidad-visual"
              aria-labelledby="form-modal-tab-identidad-visual"
            >
              <ClientLogoEditor value={clientLogo} enabled={logoEnabled}
                formTheme={formTheme}
                expanded={isMaximized}
                previewHost={previewHost}
                themeControls={<FormThemeEditor value={formTheme} onChange={(theme) => { markDirty(); setFormTheme(theme); }} />}
                clientName={clientes.find((cliente) => cliente.clienteId === clienteSelected)?.clienteNombre || "cliente seleccionado"}
                showJobOfferDetail={jobOfferDescEnabled} onBusyChange={setLogoBusy}
                onToggle={() => { markDirty(); setLogoEnabled((enabled) => !enabled); }}
                onChange={(logo) => { markDirty(); setClientLogo(logo); if (!logo) setLogoEnabled(false); }} />
            </div>
          ) : (
            <div
              role="tabpanel"
              id="form-modal-panel-configuracion"
              aria-labelledby="form-modal-tab-configuracion"
            >

          {/* Tipo de formulario */}
          <div style={{ marginBottom: "22px" }}>
            <div style={labelStyle}>Tipo de formulario</div>
            <div style={{ display: "flex", gap: "8px" }}>
              {(esTwSel
                ? (["reclutador", "cliente", "cuenta"] as TipoFormulario[])
                : (Object.keys(TIPO_LABELS) as TipoFormulario[])
              ).map((t) => {
                const active = tipo === t;
                return (
                  <button
                    key={t}
                    onClick={() => { setTipo(t); markDirty(); }}
                    onMouseEnter={(e) => {
                      if (!active) {
                        const btn = e.currentTarget as HTMLButtonElement;
                        btn.style.background = "rgba(0,101,221,0.06)";
                        btn.style.border = "1px solid #0065DD";
                        btn.style.color = "#0065DD";
                        btn.style.boxShadow = "0 1px 6px rgba(0,101,221,0.15)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!active) {
                        const btn = e.currentTarget as HTMLButtonElement;
                        btn.style.background = "#fff";
                        btn.style.border = "1px solid #e2e8f0";
                        btn.style.color = "#475569";
                        btn.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)";
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: "10px 12px",
                      borderRadius: "10px",
                      border: active ? "none" : "1px solid #e2e8f0",
                      background: active
                        ? "linear-gradient(135deg, #0050b3 0%, #0065DD 100%)"
                        : "#fff",
                      color: active ? "#fff" : "#475569",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "13px",
                      fontWeight: active ? 600 : 400,
                      cursor: "pointer",
                      boxShadow: active
                        ? "0 2px 10px rgba(0,101,221,0.3)"
                        : "0 1px 3px rgba(0,0,0,0.06)",
                      transition: "all 0.15s",
                    }}
                  >
                    {TIPO_LABELS[t]}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Selects dinámicos */}
          <AnimatePresence mode="wait">
            <motion.div
              key={tipo}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.16 }}
              style={{ marginBottom: "22px" }}
            >
              {tipo === "cliente" && (
                <div>
                  <DropDownListCustomized
                    key={`cliente-${tipo}`}
                    list={clientes.map((c) => ({ name: `${c.clienteNombre} (${c.clienteDni})`, code: c.clienteId }))}
                    layer="Cliente"
                    required={false}
                    errorPosition="down"
                    onValueChange={(vals: any) => { markDirty(); setClienteFormVals(vals); }}
                    onPermaValues={clienteFormVals}
                    onPropertyChange="cliente"
                    disabled={loadingClientes}
                    placeholder={loadingClientes ? "Cargando clientes..." : "Seleccione"}
                    fieldError={clienteErr}
                  />
                </div>
              )}

              {tipo === "cuenta" && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div>
                    <DropDownListCustomized
                      key={`cliente-${tipo}`}
                      list={clientes.map((c) => ({ name: `${c.clienteNombre} (${c.clienteDni})`, code: c.clienteId }))}
                      layer="Cliente"
                      required={false}
                      errorPosition="down"
                      onValueChange={(vals: any) => { markDirty(); setClienteFormVals(vals); }}
                      onPermaValues={clienteFormVals}
                      onPropertyChange="cliente"
                      disabled={loadingClientes}
                      placeholder={loadingClientes ? "Cargando clientes..." : "Seleccione"}
                      fieldError={clienteErr}
                    />
                  </div>
                  <div>
                    <DropDownListCustomized
                      key={`area-${clienteSelected}`}
                      list={areaOptions.map((a) => ({ name: a.areaNegocioCod, code: a.areaNegocioCod }))}
                      layer="Área de negocio"
                      required={false}
                      errorPosition="down"
                      onValueChange={(vals: any) => { markDirty(); setAreaFormVals(vals); }}
                      onPermaValues={areaFormVals}
                      onPropertyChange="area"
                      disabled={!clienteSelected || loadingAreas}
                      placeholder={
                        !clienteSelected
                          ? "Primero selecciona un cliente"
                          : loadingAreas
                          ? "Cargando áreas..."
                          : "Seleccione"
                      }
                      fieldError={areaErr}
                    />
                    {loadingAreas && clienteSelected && (
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "5px" }}>
                        <CircularProgress size={11} style={{ color: "#0065DD" }} />
                        <span style={{ fontFamily: "Roboto, sans-serif", fontSize: "11px", color: "#64748b" }}>
                          Cargando áreas de negocio...
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {tipo === "reclutador" && (
                <div>
                  {/* Línea divisoria */}
                  <div style={{ borderTop: "1px solid #e2e8f0", marginBottom: "14px" }} />

                  {/* Toggle "Asignar a mi" — sólo para perfiles admin */}
                  {esAdminSel && (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                      <label style={{ fontSize: "13px", color: "#64748b", fontWeight: 500, cursor: "pointer" }}
                             onClick={() => handleToggleAsignarAMi()}>
                        Asignar a mi
                      </label>
                      <div
                        onClick={() => handleToggleAsignarAMi()}
                        style={{
                          width: "36px",
                          height: "20px",
                          borderRadius: "999px",
                          background: asignarAMi ? "#2563eb" : "#cbd5e1",
                          cursor: "pointer",
                          position: "relative",
                          transition: "background 0.2s",
                          flexShrink: 0,
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            top: "2px",
                            left: asignarAMi ? "18px" : "2px",
                            width: "16px",
                            height: "16px",
                            borderRadius: "50%",
                            background: "#fff",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                            transition: "left 0.2s",
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Dropdowns alineados */}
                  <div style={{ display: "grid", gridTemplateColumns: esAdminSel ? "1fr 1fr" : "1fr", gap: "12px" }}>
                    {esAdminSel && (
                      <DropDownListCustomized
                        key={asignarAMi ? "reclutador-locked" : `reclutador-unlocked-${reclutadorSaved?.code ?? "empty"}`}
                        list={reclutadores.map((r) => ({ name: r.usuarioNombre, code: r.usuarioId }))}
                        layer={loadingReclutadores ? "Cargando..." : "Reclutador"}
                        required={false}
                        errorPosition="down"
                        onValueChange={(vals: any) => { markDirty(); setReclutadorFormVals(vals); }}
                        onPermaValues={reclutadorFormVals}
                        onPropertyChange="reclutador"
                        placeholder={loadingReclutadores ? "Cargando..." : "Seleccione"}
                        disabled={asignarAMi || loadingReclutadores}
                        defaultValue={asignarAMi ? { name: "Asignado a ti", code: "__self__" } : (reclutadorSaved ?? undefined)}
                        fieldError={reclutadorErr && !asignarAMi}
                      />
                    )}
                    <DropDownListCustomized
                      key={`proceso-${tipo}-${asignarAMi ? "__self__" : reclutadorSelected}`}
                      list={procesosAsignados.map((p) => ({
                        name: `${p.busquedaId} - ${p.areaNegocioCod} - ${p.cargoNombre}`,
                        code: p.busquedaId,
                      }))}
                      layer="Proceso"
                      required={false}
                      errorPosition="down"
                      onValueChange={(vals: any) => { markDirty(); setProcesoFormVals(vals); }}
                      onPermaValues={procesoFormVals}
                      onPropertyChange="proceso"
                      placeholder={loadingProcesos ? "Cargando..." : (!reclutadorSelected && !asignarAMi) ? "Seleccione un reclutador" : "Seleccione"}
                      disabled={loadingProcesos || (!asignarAMi && !reclutadorSelected)}
                      fieldError={procesoErr}
                      onSearch={handleProcesoSearch}
                      onScrollEnd={handleProcesoLoadMore}
                      loadingMore={loadingMoreProcesos}
                    />
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          <div style={{ marginBottom: "22px" }}>
            <DropDownListCustomized
              list={categoriasFormulario.map((categoria) => ({ name: categoria.name, code: categoria.formCategoryId }))}
              layer="Categoría de formulario"
              summary="(opcional)"
              required={false}
              errorPosition="down"
              onValueChange={(vals: any) => { markDirty(); setCategoriaFormVals(vals); }}
              onPermaValues={categoriaFormVals}
              onPropertyChange="categoria"
              disabled={loadingCategoriasFormulario}
              placeholder={loadingCategoriasFormulario ? "Cargando categorías..." : "Sin categoría de formulario"}
            />
          </div>

          <div style={{ marginBottom: "22px" }}>
            <DropDownListCustomized
              list={fuentesReclutamiento.map((fuente) => ({ name: fuente.name, code: fuente.recruitmentSourceId }))}
              layer="Fuente de reclutamiento"
              summary="(opcional)"
              required={false}
              errorPosition="down"
              onValueChange={(vals: any) => { markDirty(); setFuenteFormVals(vals); }}
              onPermaValues={fuenteFormVals}
              onPropertyChange="fuente"
              disabled={loadingFuentesReclutamiento}
              placeholder={loadingFuentesReclutamiento ? "Cargando fuentes..." : "Sin fuente de reclutamiento"}
            />
          </div>

          {/* ── Descripciones opcionales ── */}
          <DescriptionSection
            title="Descripción del formulario"
            hint="Nota interna sobre el objetivo o propósito del formulario. No se muestra al postulante."
            enabled={formDescEnabled}
            onToggleEnabled={() => { markDirty(); setFormDescEnabled((v) => !v); }}
          >
            <PlainDescriptionField
              value={formDescription}
              onChange={(v) => { markDirty(); setFormDescription(v); }}
            />
          </DescriptionSection>

          <DescriptionSection
            title="Descripción de oferta laboral"
            hint="Detalle de la oferta visible para el postulante antes y durante la postulación."
            enabled={jobOfferDescEnabled}
            onToggleEnabled={() => { markDirty(); setJobOfferDescEnabled((v) => !v); }}
          >
            <RichTextEditor
              value={jobOfferDescription}
              onChange={(html) => { markDirty(); setJobOfferDescription(html); }}
              maxLength={JOB_OFFER_DESCRIPTION_MAX}
              placeholder="Describe la vacante: responsabilidades, requisitos, beneficios…"
              minHeight={180}
            />
          </DescriptionSection>

          <div style={{ marginBottom: "22px" }}>
            <div style={labelStyle}>Modo de formulario</div>
            <div style={{ display: "flex", gap: "8px" }}>
              {(["completo", "dividido"] as ModoFormulario[]).map((m) => {
                const active = modoFormulario === m;
                return (
                  <button
                    key={m}
                    onClick={() => setModoFormulario(m)}
                    onMouseEnter={(e) => {
                      if (!active) {
                        const btn = e.currentTarget as HTMLButtonElement;
                        btn.style.background = "rgba(0,101,221,0.06)";
                        btn.style.border = "1px solid #0065DD";
                        btn.style.color = "#0065DD";
                        btn.style.boxShadow = "0 1px 6px rgba(0,101,221,0.15)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!active) {
                        const btn = e.currentTarget as HTMLButtonElement;
                        btn.style.background = "#fff";
                        btn.style.border = "1px solid #e2e8f0";
                        btn.style.color = "#475569";
                        btn.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)";
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: "10px 12px",
                      borderRadius: "10px",
                      border: active ? "none" : "1px solid #e2e8f0",
                      background: active
                        ? "linear-gradient(135deg, #0050b3 0%, #0065DD 100%)"
                        : "#fff",
                      color: active ? "#fff" : "#475569",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "13px",
                      fontWeight: active ? 600 : 400,
                      cursor: "pointer",
                      boxShadow: active
                        ? "0 2px 10px rgba(0,101,221,0.3)"
                        : "0 1px 3px rgba(0,0,0,0.06)",
                      transition: "all 0.15s",
                    }}
                  >
                    {m === "completo" ? "Formulario completo" : "Formulario por pasos"}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Step tabs (dividido mode only) ── */}
          <AnimatePresence>
            {isDividido && (
              <motion.div
                initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                animate={{ opacity: 1, height: "auto", marginBottom: 16 }}
                exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                transition={{ duration: 0.2 }}
                style={{ overflow: "hidden" }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: "0",
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "10px",
                    padding: "3px",
                  }}
                >
                  {([1, 2] as (1 | 2)[]).map((paso) => {
                    const active = pasoActivo === paso;
                    // Count enabled fields for the badge
                    const stepCampos = paso === 1 ? campos : camposPaso2;
                    const stepDocs   = paso === 1 ? documentos : documentosPaso2;
                    const count =
                      CAMPOS.filter((c) => stepCampos[c.key].enabled).length +
                      DOCUMENTOS.filter((d) => stepDocs[d.key].enabled).length;

                    return (
                      <button
                        key={paso}
                        onClick={() => setPasoActivo(paso)}
                        style={{
                          flex: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "7px",
                          padding: "8px 14px",
                          borderRadius: "8px",
                          border: "none",
                          background: active ? "#fff" : "transparent",
                          color: active ? "#0065DD" : "#64748b",
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "13px",
                          fontWeight: active ? 600 : 400,
                          cursor: "pointer",
                          boxShadow: active ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                          transition: "all 0.15s",
                        }}
                      >
                        Paso {paso}
                        {count > 0 && (
                          <span
                            style={{
                              fontSize: "11px",
                              fontWeight: 600,
                              background: active ? "rgba(0,101,221,0.1)" : "#e2e8f0",
                              color: active ? "#0065DD" : "#64748b",
                              padding: "1px 6px",
                              borderRadius: "10px",
                              minWidth: "20px",
                              textAlign: "center",
                            }}
                          >
                            {count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <div
                  style={{
                    marginTop: "8px",
                    padding: "10px 12px",
                    borderRadius: "9px",
                    background: "rgba(0,101,221,0.05)",
                    border: "1px solid rgba(0,101,221,0.14)",
                    display: "flex",
                    gap: "9px",
                    alignItems: "flex-start",
                  }}
                >
                  {/* info circle */}
                  <div
                    style={{
                      width: "16px",
                      height: "16px",
                      borderRadius: "50%",
                      background: "#0065DD",
                      color: "#fff",
                      fontSize: "10px",
                      fontWeight: 700,
                      fontFamily: "Roboto, sans-serif",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      marginTop: "1px",
                      letterSpacing: 0,
                    }}
                  >
                    i
                  </div>
                  <div>
                    <div
                      style={{
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#0050b3",
                        marginBottom: "3px",
                      }}
                    >
                      Formulario incremental
                    </div>
                    <div
                      style={{
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "11.5px",
                        color: "#1e40af",
                        lineHeight: 1.55,
                      }}
                    >
                      {pasoActivo === 1
                        ? "Configura los campos que verá el postulante en el primer paso."
                        : "El Paso 2 incluye automáticamente todos los campos del Paso 1. Aquí solo configuras los campos adicionales que se mostrarán al avanzar."}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Bulk actions ── */}
          <div style={{ display: "flex", justifyContent: "space-between", gap: "8px", marginBottom: "14px" }}>
            <button
              onClick={toggleAllEnabled}
              style={{
                padding: "5px 12px",
                borderRadius: "7px",
                border: "1px solid #e2e8f0",
                background: "#fff",
                color: "#475569",
                fontFamily: "Roboto, sans-serif",
                fontSize: "12px",
                fontWeight: 500,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = "#0065DD"; (e.currentTarget as HTMLButtonElement).style.color = "#0065DD"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = "#e2e8f0"; (e.currentTarget as HTMLButtonElement).style.color = "#475569"; }}
            >
              {allEnabled ? "Desmarcar todos" : "Marcar todos"}
            </button>
            <button
              onClick={toggleAllRequired}
              style={{
                padding: "5px 12px",
                borderRadius: "7px",
                border: "1px solid #e2e8f0",
                background: "#fff",
                color: "#475569",
                fontFamily: "Roboto, sans-serif",
                fontSize: "12px",
                fontWeight: 500,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = "#b45309"; (e.currentTarget as HTMLButtonElement).style.color = "#b45309"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = "#e2e8f0"; (e.currentTarget as HTMLButtonElement).style.color = "#475569"; }}
            >
              {allRequired ? "Nada obligatorio" : "Todo obligatorio"}
            </button>
          </div>

          {/* ── Aviso: campos obligatorios por defecto ── */}
          <div
            style={{
              marginBottom: "14px",
              borderRadius: "9px",
              background: "rgba(180,83,9,0.05)",
              border: "1px solid rgba(180,83,9,0.18)",
              overflow: "hidden",
            }}
          >
            <button
              onClick={() => setShowDefaultFields((v) => !v)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: "9px",
                padding: "10px 12px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                textAlign: "left",
                outline: "none",
              }}
            >
              {/* info circle */}
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  borderRadius: "50%",
                  background: "#b45309",
                  color: "#fff",
                  fontSize: "10px",
                  fontWeight: 700,
                  fontFamily: "Roboto, sans-serif",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  letterSpacing: 0,
                }}
              >
                i
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#92400e",
                  }}
                >
                  Hay campos obligatorios que no se muestran aquí
                </div>
                <div
                  style={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "11px",
                    color: "#b45309",
                    marginTop: "1px",
                  }}
                >
                  {showDefaultFields ? "Ocultar lista" : "Ver lista de campos siempre obligatorios"}
                </div>
              </div>
              {/* chevron */}
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#b45309"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  flexShrink: 0,
                  transform: showDefaultFields ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            <AnimatePresence initial={false}>
              {showDefaultFields && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  style={{ overflow: "hidden" }}
                >
                  <div
                    style={{
                      padding: "0 12px 12px 37px",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "11.5px",
                      color: "#78350f",
                      lineHeight: 1.6,
                    }}
                  >
                    <div style={{ marginBottom: "6px" }}>
                      Estos campos vienen por defecto en el formulario y siempre serán obligatorios para el postulante:
                    </div>
                    <ul
                      style={{
                        margin: 0,
                        paddingLeft: "18px",
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "2px 16px",
                      }}
                    >
                      {CAMPOS_OBLIGATORIOS_DEFAULT.map((c) => (
                        <li key={c} style={{ color: "#78350f" }}>{c}</li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* ── Campos ── */}
          <SectionHeader title="Campos" />
          <div style={{ display: "flex", flexDirection: "column", gap: "1px", marginBottom: "20px" }}>
            {CAMPOS.map((item) => {
              const isOpcionCampo = item.key === "turno" || item.key === "cargo";
              const showEditor =
                isOpcionCampo &&
                !lockedCampoKeys.has(item.key) &&
                activeCampos[item.key].enabled;
              return (
                <React.Fragment key={item.key}>
                  <FieldRow
                    item={item}
                    state={activeCampos}
                    onToggle={activeToggleCampo}
                    lockedByLabel={lockedCampoKeys.has(item.key) ? otherStepLabel : undefined}
                  />
                  {showEditor && (
                    <MasterOptionsSelector
                      label={item.label}
                      catalog={catalogos[item.key as OpcionCampoKey]}
                      selected={seleccion[item.key as OpcionCampoKey]}
                      loading={loadingCatalogos}
                      adding={addingField === (item.key as OpcionCampoKey)}
                      error={
                        showOptionErrors &&
                        seleccion[item.key as OpcionCampoKey].length === 0
                      }
                      onToggle={(ci) => toggleOption(item.key as OpcionCampoKey, ci)}
                      onAdd={(name) => addOption(item.key as OpcionCampoKey, name)}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* ── Documentos ── */}
          <SectionHeader title="Documentos" />
          <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>
            {DOCUMENTOS.map((item) => (
              <FieldRow
                key={item.key}
                item={item}
                state={activeDocumentos}
                onToggle={activeToggleDocumento}
                lockedByLabel={lockedDocKeys.has(item.key) ? otherStepLabel : undefined}
              />
            ))}
          </div>
          </div>))}
        </div>

        {/* ── Footer ── */}
        <div
          style={{
            padding: "16px 24px",
            borderTop: "1px solid #f1f5f9",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            background: "#fafafa",
          }}
        >
          <AnimatePresence>
            {error && (
              <motion.div
                key="error-banner"
                initial={{ opacity: 0, y: -6, height: 0 }}
                animate={{ opacity: 1, y: 0, height: "auto" }}
                exit={{ opacity: 0, y: -6, height: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                style={{
                  overflow: "hidden",
                  padding: "9px 14px",
                  borderRadius: "8px",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "12.5px",
                  color: "#b91c1c",
                }}
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
            <button
              onClick={handleRequestClose}
              style={{
                padding: "9px 20px",
                borderRadius: "9px",
                border: "1px solid #e2e8f0",
                background: "#fff",
                color: "#475569",
                fontFamily: "Roboto, sans-serif",
                fontSize: "13px",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              {successData ? "Cerrar" : "Cancelar"}
            </button>
            {!successData && (
              <motion.button
                whileHover={{ scale: loading ? 1 : 1.02 }}
                whileTap={{ scale: loading ? 1 : 0.97 }}
                onClick={handleCrear}
                disabled={loading}
                style={{
                  padding: "9px 22px",
                  borderRadius: "9px",
                  border: "none",
                  background: loading
                    ? "#94a3b8"
                    : "linear-gradient(135deg, #0050b3 0%, #0065DD 100%)",
                  color: "#fff",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: loading ? "not-allowed" : "pointer",
                  boxShadow: loading ? "none" : "0 2px 10px rgba(0,101,221,0.3)",
                  transition: "background 0.2s, box-shadow 0.2s",
                }}
              >
                {loading ? "Creando..." : "Crear formulario"}
              </motion.button>
            )}
          </div>
        </div>

        {/* ── Confirm close dialog ── */}
        {showConfirmClose && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(15,23,42,0.45)",
              backdropFilter: "blur(4px)",
              WebkitBackdropFilter: "blur(4px)",
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "18px",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.93 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: "spring", stiffness: 320, damping: 24 }}
              style={{
                background: "#fff",
                borderRadius: "14px",
                padding: "28px 28px 22px",
                maxWidth: "360px",
                width: "90%",
                boxShadow: "0 16px 48px rgba(15,23,42,0.25)",
              }}
            >
              <div style={{ fontFamily: "Roboto, sans-serif", fontSize: "15px", fontWeight: 700, color: "#1e293b", marginBottom: "10px" }}>
                ¿Descartar cambios?
              </div>
              <div style={{ fontFamily: "Roboto, sans-serif", fontSize: "13px", color: "#64748b", marginBottom: "22px", lineHeight: 1.55 }}>
                Tienes cambios sin guardar. Si cierras el formulario, se perderán todos los cambios realizados.
              </div>
              <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                <button
                  onClick={() => setShowConfirmClose(false)}
                  style={{
                    padding: "8px 18px",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    background: "#fff",
                    color: "#475569",
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "13px",
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  Seguir editando
                </button>
                <button
                  onClick={onClose}
                  style={{
                    padding: "8px 18px",
                    borderRadius: "8px",
                    border: "none",
                    background: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)",
                    color: "#fff",
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "13px",
                    fontWeight: 600,
                    cursor: "pointer",
                    boxShadow: "0 2px 8px rgba(220,38,38,0.3)",
                  }}
                >
                  Descartar y cerrar
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default CrearFormularioModal;
